import React, {useEffect, useState} from "react";
import {
    Badge,
    Button,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Step,
    StepButton,
    StepContent,
    Stepper,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import AlertTypeSelectionCards from "./AlertTypeSelectionCards";
import {SafeHtml} from "../../components/security/SafeHtml";
import SelectField from "../../components/InputFields/SelectField";
import InputField from "../../components/InputFields/InputField";
import {
    AlertCategoryType,
    AlertDto,
    AlertSendStatus,
    AlertSendType,
    AlertTemplate,
    CommunicationChannel,
    NotifiedCustomers
} from "../../interfaces/IAlert";
import DateTimePickerField from "../../components/InputFields/DateTimePickerField";
import AlertCustomerSelectionCards from "./AlertCustomerSelectionCards";
import AlertTemplateSelectField from "./AlertTemplateSelectField";
import {toDateTime} from "../../utils/ValueFormatters";
import AlertPreviewDialog from "./AlertPreviewDialog";
import {useParams} from "react-router-dom";
import AlertEditor from "./AlertEditor";
import AlertLanguageTabs from "../../components/data-display/AlertLanguageTabs";
import EditableFields from "../../components/InputFields/EditableFields";
import {AlertSnippetDto, EventDto} from "../../interfaces/IEvent";
import {LoadingIndicator} from "@eidsivabredband/react-component-ui-package";

interface AlertStepsProps {
    currentAlert: AlertDto;
    templates: AlertTemplate[];
    viewEvent: EventDto;
    handleInputChange: (name: string, value: any) => void;
    handleTemplateInputChange: (name: string, value: any) => void;
    handleSelectTemplateChange: (value: Date | string | object | undefined) => void;
    handleEditorChange: (editorRef: any) => void;
    handleEnglishEditorChange: (editorRef: any) => void;
    handleEnglishTemplateInputChange: (name: string, value: string) => void;
    handleReplaceContentText: (name: string, value: Date | string) => void;
    onSelectedCustomersChange: (selectedCustomers: NotifiedCustomers) => void;
    onIsDraftChange: (isDraft: boolean) => void;
    customerAlertHistory?: Map<string, AlertSnippetDto[]>;
    customerLanguages: Record<string, string>;
    onSetCustomerLanguage: (customerNumber: string, language: string) => void;
    sendLoading: boolean;
}

interface CustomStep {
    label: string;
    content: React.ReactNode;
}

const MAX_SMS_LENGTH = 400;

function AlertSteps({
        currentAlert,
        templates,
        viewEvent,
        handleInputChange,
        handleTemplateInputChange,
        handleSelectTemplateChange,
        handleEditorChange,
        handleEnglishEditorChange,
        handleEnglishTemplateInputChange,
        handleReplaceContentText,
        onSelectedCustomersChange,
        onIsDraftChange,
        customerAlertHistory,
        customerLanguages,
        onSetCustomerLanguage,
        sendLoading
    }: AlertStepsProps) {
    const paramIds = useParams();
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [activeLanguageTab, setActiveLanguageTab] = useState(0);
    const [summaryLanguage, setSummaryLanguage] = useState(0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };

    const alertCategory = () => {
        if(currentAlert.category){
            switch(currentAlert.category){
                case AlertCategoryType.GenericAlert:
                    return "Generell varsel";
                case AlertCategoryType.CustomerAlert:
                    return "Kundevarsel";
                case AlertCategoryType.StakeholderAlert:
                    return "Stakeholdervarsel";
            }
        }
    };

    const isGSM7AndSpaceAndNorwegianCharacters = (text: string) => {
        const gsm7Characters = "@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}[~]|€";

        for(let i = 0; i < text?.length; i++){
            if(!gsm7Characters.includes(text[i])){
                return false;
            }
        }

        return true;
    };

    const isSmsContentValid = (template: AlertTemplate) =>
        isGSM7AndSpaceAndNorwegianCharacters(template.editedText) && template.editedText.length <= MAX_SMS_LENGTH;

    const hasAtLeastOneRecipient = () =>
        currentAlert.notifiedCustomers?.companyCustomerIds?.length > 0 ||
        currentAlert.notifiedCustomers?.companyWithSlaCustomerIds?.length > 0 ||
        currentAlert.notifiedCustomers?.privateCustomerIds?.length > 0 ||
        currentAlert.phoneNumbers?.length > 0 ||
        currentAlert.notifiedCustomers?.allCompanyCustomers ||
        currentAlert.emailAddresses?.length > 0;

    const hasMetRequiredFields = (activeStep: number) => {
        switch (activeStep) {
            case 0: // Velg varseltype
                if (!currentAlert.type) return false;
                // Drafts don't require a send date even for Scheduled type
                if (currentAlert.status === AlertSendStatus.Draft) return true;
                return currentAlert.type === AlertSendType.Immediate ||
                    (currentAlert.type === AlertSendType.Scheduled && currentAlert.sendOn);
            case 1: // Velg berørte kunder — always valid for drafts
                if (currentAlert.status === AlertSendStatus.Draft) return true;
                return hasAtLeastOneRecipient();
            case 2: // Velg mal
                if(currentAlert.channel === CommunicationChannel.Sms){
                    if(!isSmsContentValid(currentAlert.template)) return false;
                    if(currentAlert.englishTemplate && !isSmsContentValid(currentAlert.englishTemplate)) return false;
                }

                if(currentAlert.template.text.length > 0){
                    if(Object.keys(currentAlert.template.editableFields).length === 0){
                        return true;
                    }

                    if(Object.values(currentAlert.template.editableFields).every(x => (x !== null && x !== ''))){
                        return true;
                    }
                }

                return false;
            case 3: // Oppsummering
                if (!currentAlert.description) return false;
                if (currentAlert.status === AlertSendStatus.Draft) return true;
                return hasAtLeastOneRecipient();
            default:
                return false;
        }
    };

    const englishNeedsAttention =
        !!currentAlert.englishTemplate &&
        currentAlert.channel === CommunicationChannel.Sms &&
        !isSmsContentValid(currentAlert.englishTemplate);

    const summaryShown =
        summaryLanguage === 1 && currentAlert.englishTemplate ? currentAlert.englishTemplate : currentAlert.template;

    const renderLanguageEditor = (template: AlertTemplate, isEnglish: boolean) => (
        <Stack spacing={2}>
            {template.channel === CommunicationChannel.Email && (
                <InputField
                    name="subject"
                    title={isEnglish ? "Emne (engelsk)" : "Emne"}
                    size="medium"
                    value={template.subject}
                    required={!isEnglish}
                    multiline={true}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                        (isEnglish ? handleEnglishTemplateInputChange : handleTemplateInputChange)(ev.target.name, ev.target.value)
                    }
                />
            )}
            <AlertEditor
                template={template}
                onEditorChange={isEnglish ? handleEnglishEditorChange : handleEditorChange}
                setOpenPreviewDialog={() => setOpenPreviewDialog(!openPreviewDialog)}
            />
        </Stack>
    );

    const steps: CustomStep[] = [
        {
            label: "Varseltype",
            content: (
                <>
                    <AlertTypeSelectionCards
                        newAlert={currentAlert}
                        handleAlertTypeChange={handleInputChange}
                        onIsDraftChange={onIsDraftChange}
                    />
                    
                    {currentAlert.type === AlertSendType.Scheduled && (
                        <>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <DateTimePickerField
                                    name="sendOn"
                                    title="Sendetidspunkt"
                                    value={new Date(currentAlert.sendOn)}
                                    required={true}
                                    onChange={(date: Date) =>
                                        handleInputChange("sendOn", date)
                                    }
                                />
                            </Grid>
                            <Grid
                                size={{
                                    md: 6
                                }}>

                            </Grid>
                        </>
                    )}
                </>
            )
        },
        {
            label: "Berørte kunder",
            content: (
                    <Grid size={12} sx={{ mt: 1 }}>
                        <AlertCustomerSelectionCards
                            viewEvent={viewEvent}
                            currentAlert={currentAlert}
                            onSelectedCustomersChange={onSelectedCustomersChange}
                            customerAlertHistory={customerAlertHistory}
                            customerLanguages={customerLanguages}
                            onSetCustomerLanguage={onSetCustomerLanguage}
                        />
                    </Grid>
            )
        },
        {
            label: "Mal",
            content: (
                <>
                    <Grid size={12}>
                        <AlertTemplateSelectField 
                            name="template"
                            templates={templates}
                            placeholder={"Ingen valgt"}
                            defaultValue={""}
                            value={currentAlert.template?.id}
                            required={true}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                                handleSelectTemplateChange(ev.target.value)
                            }
                        />
                    </Grid>
                    {currentAlert?.template.id !== 0 && (
                        <>
                            <AlertPreviewDialog
                                openPreviewDialog={openPreviewDialog}
                                setOpenPreviewDialog={() => setOpenPreviewDialog(!openPreviewDialog)}
                                currentAlert={currentAlert} 
                            />
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 4
                                }}>
                                <Stack spacing={2}>
                                    <InputField
                                        name="description"
                                        title="Tittel på varsel"
                                        size="medium"
                                        value={currentAlert.description}
                                        required={true}
                                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                                            handleInputChange(ev.target.name, ev.target.value)
                                        }
                                    />
                                    {currentAlert.template.channel === CommunicationChannel.Email && (
                                        <SelectField
                                            name="sender"
                                            title="Avsender"
                                            selectOptions={["ikke-svar@digital.eidsiva.no"]}
                                            value={currentAlert.template.sender}
                                            required={true}
                                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                                                handleTemplateInputChange(ev.target.name, ev.target.value)
                                            }
                                        />
                                    )}
                                    <EditableFields
                                        editableFields={currentAlert.template.editableFields}
                                        onReplaceContentText={handleReplaceContentText}
                                    />
                                </Stack>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: Object.keys(currentAlert.template.editableFields).length === 0 ? 12 : 8
                                }}>
                                {currentAlert.englishTemplate ? (
                                    <>
                                        <Tabs
                                            value={activeLanguageTab}
                                            onChange={(_event, value) => setActiveLanguageTab(value)}
                                            sx={{ mb: 2 }}
                                        >
                                            <Tab label="Norsk" />
                                            <Tab label={
                                                <Badge color="error" variant="dot" invisible={!englishNeedsAttention} sx={{ pr: 0.5 }}>
                                                    Engelsk
                                                </Badge>
                                            } />
                                        </Tabs>
                                        {activeLanguageTab === 0
                                            ? renderLanguageEditor(currentAlert.template, false)
                                            : renderLanguageEditor(currentAlert.englishTemplate, true)}
                                    </>
                                ) : (
                                    renderLanguageEditor(currentAlert.template, false)
                                )}
                            </Grid>
                        </>
                    )}
                </>
            )
        },
        {
            label: "Oppsummering",
            content: (
                <>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 5
                        }}>
                        <Paper sx={{ p:2 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    textDecoration: 'underline'
                                }}>
                                Varseldetaljer
                            </Typography>
                            <List >
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary={"Navn"}
                                        secondary={
                                            !currentAlert.description && 
                                            <Typography variant="caption" sx={{ color: "red" }}>Mangler navn på varsel</Typography>
                                        }
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }}
                                    />
                                    <ListItemText
                                        sx={{textAlign: 'right'}}
                                        secondary={currentAlert.description}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                </ListItem>
                                <Divider />
                                {currentAlert.id && (
                                    <>
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={"Varselid"}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                        <ListItemText
                                            sx={{textAlign: 'right'}}
                                            secondary={currentAlert.id}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                    </ListItem>
                                    <Divider />
                                    </>
                                )}
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary={currentAlert.isSent ? "Sendt ut" : "Sendes ut"}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                    <ListItemText
                                        sx={{textAlign: 'right'}}
                                        secondary={currentAlert.type === AlertSendType.Immediate 
                                            ? "Umiddelbart" 
                                            : currentAlert.sendOn ? toDateTime(new Date(currentAlert.sendOn)) : null
                                        }
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary={"Kategori"}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                    <ListItemText
                                        sx={{textAlign: 'right'}}
                                        secondary={alertCategory()}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary={"Avsender"}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                    <ListItemText
                                        sx={{textAlign: 'right'}}
                                        secondary={currentAlert.channel === CommunicationChannel.Email ? currentAlert.template.sender : "03370"}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                </ListItem>
                                <Divider />
                                {currentAlert.isSent && (
                                    <>
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={"Opprettet"}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                        <ListItemText
                                            sx={{textAlign: 'right'}}
                                            secondary={toDateTime(new Date(currentAlert.createdOn))}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                    </ListItem>
                                    <Divider />
                                    </>
                                )}
                                {currentAlert.createdBy?.name && (
                                    <>
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={"Opprettet av"}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                        <ListItemText
                                            sx={{textAlign: 'right'}}
                                            secondary={currentAlert.createdBy?.name}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                    </ListItem>
                                    <Divider />
                                    </>
                                )}
                                <ListItem disableGutters>
                                    <ListItemText
                                        primary={"Antall valgt"}
                                        slotProps={{
                                            primary: { 
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }} 
                                    />
                                    <ListItemText
                                        sx={{textAlign: 'right'}}
                                        secondary={(() => {
                                            const totalCount =
                                                (currentAlert.notifiedCustomers.companyCustomerIds?.length ?? 0) +
                                                (currentAlert.notifiedCustomers.companyWithSlaCustomerIds?.length ?? 0) +
                                                (currentAlert.notifiedCustomers.privateCustomerIds?.length ?? 0) +
                                                (currentAlert.phoneNumbers?.length ?? 0) +
                                                (currentAlert.emailAddresses?.length ?? 0);
                                            if (currentAlert.notifiedCustomers.allCompanyCustomers) return 'Alle bedriftskunder';
                                            return `${totalCount}`;
                                        })()}
                                        slotProps={{
                                            primary: {
                                                variant: 'body2',
                                                sx: { fontWeight: 600, whiteSpace: 'normal' }
                                            }
                                        }}
                                    />
                                </ListItem>
                                {customerAlertHistory && customerAlertHistory.size > 0 && (() => {
                                    const allSelected = [
                                        ...(currentAlert.notifiedCustomers.companyCustomerIds ?? []),
                                        ...(currentAlert.notifiedCustomers.companyWithSlaCustomerIds ?? []),
                                        ...(currentAlert.notifiedCustomers.privateCustomerIds ?? []),
                                        ...(currentAlert.phoneNumbers ?? []),
                                        ...(currentAlert.emailAddresses ?? []),
                                    ];
                                    const previouslyNotified = allSelected.filter((id) => customerAlertHistory.has(id)).length;
                                    const newRecipients = allSelected.length - previouslyNotified;
                                    if (allSelected.length === 0) return null;
                                    return (
                                        <>
                                            <Divider />
                                            <ListItem disableGutters>
                                                <ListItemText
                                                    slotProps={{
                                                        primary: {
                                                            variant: 'body2',
                                                            sx: { fontWeight: 600, whiteSpace: 'normal' },
                                                        },
                                                    }}
                                                    primary={"Nye / tidligere varslet"}
                                                />
                                                <ListItemText
                                                    sx={{textAlign: 'right'}}
                                                    slotProps={{
                                                        secondary: {
                                                            variant: 'body2',
                                                            sx: { fontWeight: 600, whiteSpace: 'normal' },
                                                        },
                                                    }}
                                                    secondary={`${newRecipients} nye / ${previouslyNotified} tidligere varslet`}
                                                />
                                            </ListItem>
                                        </>
                                    );
                                })()}
                                {currentAlert.notifiedCustomersCount > 0 && (
                                    <>
                                    <Divider />
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={"Antall kunder varslet"}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                        <ListItemText
                                            sx={{textAlign: 'right'}}
                                            secondary={currentAlert.notifiedCustomersCount}
                                            slotProps={{
                                                primary: { 
                                                    variant: 'body2',
                                                    sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                }
                                            }} 
                                        />
                                    </ListItem>
                                    </>
                                )}
                                        <Divider />
                                        <ListItem disableGutters>
                                            <ListItemText
                                                primary={"Kommunikasjonskanal"}
                                                slotProps={{
                                                    primary: {
                                                        variant: 'body2',
                                                        sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                    }
                                                }}
                                            />
                                            <ListItemText
                                                sx={{textAlign: 'right'}}
                                                secondary={currentAlert.channel === CommunicationChannel.Email ? "E-post" : "SMS"}
                                                slotProps={{
                                                    primary: {
                                                        variant: 'body2',
                                                        sx: { fontWeight: 600, whiteSpace: 'normal' }
                                                    }
                                                }}
                                            />
                                        </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 7
                        }}>
                            <Paper sx={{p:2}}>
                                <Typography
                                    variant="body1"
                                    color={summaryShown?.editedText?.length > MAX_SMS_LENGTH && currentAlert.channel === CommunicationChannel.Sms ? "error" : "text.primary"}
                                    sx={{
                                        fontWeight: 600,
                                        mb: 2,
                                        textAlign: 'center',
                                        textDecoration: 'underline'
                                    }}>
                                    {currentAlert.channel === CommunicationChannel.Sms &&
                                            `Varselmelding (${summaryShown?.editedText?.length} / ${MAX_SMS_LENGTH} tegn)`
                                    }
                                    {currentAlert.channel === CommunicationChannel.Email && `Forhåndsvisning av e-post`}
                                </Typography>
                                {currentAlert.englishTemplate && (
                                    <AlertLanguageTabs value={summaryLanguage} onChange={setSummaryLanguage} />
                                )}
                                <Typography variant="body2">
                                    <SafeHtml element="span" html={summaryShown?.editedText} />
                                </Typography>
                            </Paper>
                    </Grid>
                </>
            )
        }
    ];

    useEffect(() => {
        setActiveLanguageTab(0);
        setSummaryLanguage(0);
    }, [currentAlert.template?.id]);

    useEffect(() => {
        if(paramIds.alertId){
            setActiveStep(steps.length - 1);
        }
    }, [paramIds.alertId, steps.length]);

    return (
        <>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepButton color="inherit" onClick={sendLoading ? undefined : handleStep(index)}>
                            {step.label}
                        </StepButton>
                        <StepContent>
                            <Grid container rowSpacing={4} columnSpacing={3} sx={{
                                flexDirection: "row"
                            }}>
                                {step.content}
                                <Grid size={12}>
                                    <Stack spacing={2} direction={"row"}>
                                        <Button
                                            disabled={index === 0 || sendLoading}
                                            variant="contained"
                                            color="warning"
                                            size="large"
                                            onClick={handleBack}
                                        >
                                            Tilbake
                                        </Button>
                                        {index === steps.length - 1 ? (
                                            <Button
                                                color="success"
                                                variant="contained"
                                                size="large"
                                                type="submit"
                                                startIcon={sendLoading ? <LoadingIndicator size={'xsmall'} /> : null}
                                                disabled={!hasMetRequiredFields(index) ? true : sendLoading}
                                            >
                                                {currentAlert.status === AlertSendStatus.Draft ? "Lagre utkast" : currentAlert.type === AlertSendType.Immediate ? "Send nå" : "Lagre"}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={handleNext}
                                                disabled={!hasMetRequiredFields(index) ? true : false}
                                            >
                                                Neste
                                            </Button>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </>
    );
}

export default AlertSteps;

